import * as THREE from 'three';

export class SpeedEffects {
  readonly root = new THREE.Group();
  private readonly streaks: THREE.LineSegments;
  private readonly smoke: THREE.Points;
  private readonly streakPositions: Float32Array;
  private readonly smokePositions: Float32Array;
  private readonly smokeLife: Float32Array;
  private smokeCursor = 0;
  private boostPulse = 0;

  constructor(accentColor: number) {
    const streakCount = 54;
    this.streakPositions = new Float32Array(streakCount * 2 * 3);
    const streakGeometry = new THREE.BufferGeometry();
    streakGeometry.setAttribute('position', new THREE.BufferAttribute(this.streakPositions, 3));
    const streakMaterial = new THREE.LineBasicMaterial({ color: accentColor, transparent: true, opacity: 0, depthWrite: false });
    this.streaks = new THREE.LineSegments(streakGeometry, streakMaterial);
    this.streaks.frustumCulled = false;
    this.root.add(this.streaks);

    const smokeCount = 72;
    this.smokePositions = new Float32Array(smokeCount * 3);
    this.smokeLife = new Float32Array(smokeCount);
    for (let index = 0; index < smokeCount; index += 1) this.smokePositions[index * 3 + 1] = -1000;
    const smokeGeometry = new THREE.BufferGeometry();
    smokeGeometry.setAttribute('position', new THREE.BufferAttribute(this.smokePositions, 3));
    const smokeMaterial = new THREE.PointsMaterial({
      color: 0xd7e3e8,
      size: 0.55,
      transparent: true,
      opacity: 0.42,
      depthWrite: false,
      sizeAttenuation: true,
    });
    this.smoke = new THREE.Points(smokeGeometry, smokeMaterial);
    this.smoke.frustumCulled = false;
    this.root.add(this.smoke);
  }

  triggerBoost(): void {
    this.boostPulse = 1;
  }

  update(
    delta: number,
    elapsed: number,
    position: THREE.Vector3,
    forward: THREE.Vector3,
    right: THREE.Vector3,
    speed: number,
    sliding: number,
    grounded: boolean,
  ): void {
    this.boostPulse = Math.max(0, this.boostPulse - delta * 1.6);
    const intensity = THREE.MathUtils.smoothstep(speed, 24, 58) * 0.42 + this.boostPulse * 0.75;
    const streakMaterial = this.streaks.material as THREE.LineBasicMaterial;
    streakMaterial.opacity = Math.min(0.8, intensity);
    const count = this.streakPositions.length / 6;
    for (let index = 0; index < count; index += 1) {
      const seed = index * 17.71;
      const lateral = Math.sin(seed * 3.1) * (4 + (index % 7));
      const vertical = 0.5 + ((index * 7) % 15) * 0.42;
      const cycle = (elapsed * (18 + speed * 0.25) + seed * 11) % 42;
      const behind = 5 + cycle;
      const length = 1.5 + intensity * 8;
      const offset = index * 6;
      const startX = position.x + right.x * lateral - forward.x * behind;
      const startY = position.y + right.y * lateral - forward.y * behind + vertical;
      const startZ = position.z + right.z * lateral - forward.z * behind;
      this.streakPositions[offset] = startX;
      this.streakPositions[offset + 1] = startY;
      this.streakPositions[offset + 2] = startZ;
      this.streakPositions[offset + 3] = startX - forward.x * length;
      this.streakPositions[offset + 4] = startY - forward.y * length;
      this.streakPositions[offset + 5] = startZ - forward.z * length;
    }
    this.streaks.geometry.attributes.position.needsUpdate = true;

    if (grounded && sliding > 0.25 && speed > 8) {
      const emissions = sliding > 0.65 ? 2 : 1;
      for (let emission = 0; emission < emissions; emission += 1) {
        const index = this.smokeCursor++ % this.smokeLife.length;
        const offset = index * 3;
        this.smokePositions[offset] = position.x + right.x * (emission === 0 ? -0.85 : 0.85) - forward.x * 1.3;
        this.smokePositions[offset + 1] = position.y + 0.22;
        this.smokePositions[offset + 2] = position.z + right.z * (emission === 0 ? -0.85 : 0.85) - forward.z * 1.3;
        this.smokeLife[index] = 1;
      }
    }
    for (let index = 0; index < this.smokeLife.length; index += 1) {
      if (this.smokeLife[index] <= 0) continue;
      const offset = index * 3;
      this.smokeLife[index] = Math.max(0, this.smokeLife[index] - delta * 0.7);
      this.smokePositions[offset + 1] += delta * 0.9;
      this.smokePositions[offset] += Math.sin(index * 4.7) * delta * 0.18;
      if (this.smokeLife[index] === 0) this.smokePositions[offset + 1] = -1000;
    }
    this.smoke.geometry.attributes.position.needsUpdate = true;
    (this.smoke.material as THREE.PointsMaterial).opacity = 0.2 + sliding * 0.38;
  }

  dispose(): void {
    this.streaks.geometry.dispose();
    (this.streaks.material as THREE.Material).dispose();
    this.smoke.geometry.dispose();
    (this.smoke.material as THREE.Material).dispose();
    this.root.removeFromParent();
  }
}
